import { useCallback, useLayoutEffect } from "react";

const DEFAULT_PADDING = 8;
const DEFAULT_GAP = 6;

/**
 * Position a menu panel with position:fixed so it stays inside the viewport.
 * Long item text may ellipsis/overflow inside the panel; the panel itself stays on-screen.
 */
export function positionMenuInViewport(anchorEl, menuEl, {
    gap = DEFAULT_GAP,
    padding = DEFAULT_PADDING,
    preferAlign = "end",
    matchAnchorWidth = false
} = {}) {
    if (!anchorEl || !menuEl) {
        return;
    }

    const viewport = window.visualViewport;
    const vw = viewport?.width ?? window.innerWidth;
    const vh = viewport?.height ?? window.innerHeight;
    const offsetLeft = viewport?.offsetLeft ?? 0;
    const offsetTop = viewport?.offsetTop ?? 0;
    const maxWidth = Math.max(120, vw - padding * 2);
    const trigger = anchorEl.getBoundingClientRect();

    menuEl.style.position = "fixed";
    menuEl.style.right = "auto";
    menuEl.style.bottom = "auto";
    menuEl.style.top = "0px";
    menuEl.style.left = `${padding + offsetLeft}px`;
    menuEl.style.maxWidth = `${maxWidth}px`;
    menuEl.style.maxHeight = "none";
    menuEl.style.overflowY = "auto";
    menuEl.style.boxSizing = "border-box";
    menuEl.style.transform = "none";
    menuEl.style.width = matchAnchorWidth
        ? `${Math.min(trigger.width, maxWidth)}px`
        : "max-content";

    let menuRect = menuEl.getBoundingClientRect();
    let menuWidth = Math.min(
        Math.max(menuRect.width || menuEl.offsetWidth || 180, 160),
        maxWidth
    );
    let menuHeight = menuRect.height || menuEl.offsetHeight || 0;

    const spaceBelow = vh - (trigger.bottom - offsetTop) - padding - gap;
    const spaceAbove = (trigger.top - offsetTop) - padding - gap;
    const openBelow = menuHeight <= spaceBelow || spaceBelow >= spaceAbove;

    const maxHeight = Math.max(80, openBelow ? spaceBelow : spaceAbove);
    menuEl.style.maxHeight = `${maxHeight}px`;
    menuEl.style.width = `${menuWidth}px`;

    menuRect = menuEl.getBoundingClientRect();
    menuHeight = menuRect.height || Math.min(menuHeight, maxHeight);
    menuWidth = Math.min(menuRect.width || menuWidth, maxWidth);
    menuEl.style.width = `${menuWidth}px`;

    let top;
    if (openBelow) {
        top = trigger.bottom + gap;
        if (top + menuHeight > offsetTop + vh - padding) {
            top = Math.max(offsetTop + padding, offsetTop + vh - padding - menuHeight);
        }
    } else {
        top = trigger.top - gap - menuHeight;
        if (top < offsetTop + padding) {
            top = offsetTop + padding;
        }
    }

    let left = preferAlign === "start"
        ? trigger.left
        : trigger.right - menuWidth;

    const minLeft = offsetLeft + padding;
    const maxLeft = offsetLeft + vw - padding - menuWidth;
    left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));

    menuEl.style.top = `${Math.round(top)}px`;
    menuEl.style.left = `${Math.round(left)}px`;
    if (matchAnchorWidth) {
        menuEl.style.width = `${Math.min(trigger.width, maxWidth)}px`;
    }
}

export function clearMenuViewportPosition(menuEl) {
    if (!menuEl) {
        return;
    }
    menuEl.style.position = "";
    menuEl.style.top = "";
    menuEl.style.left = "";
    menuEl.style.right = "";
    menuEl.style.bottom = "";
    menuEl.style.width = "";
    menuEl.style.maxWidth = "";
    menuEl.style.maxHeight = "";
    menuEl.style.overflowY = "";
    menuEl.style.boxSizing = "";
    menuEl.style.transform = "";
}

/**
 * Keep an open menu panel inside the viewport on resize/scroll.
 */
export function useViewportAnchoredMenu(open, anchorRef, menuRef, options = {}) {
    const preferAlign = options.preferAlign;
    const gap = options.gap;
    const padding = options.padding;
    const matchAnchorWidth = options.matchAnchorWidth;

    const update = useCallback(() => {
        if (!open) {
            return;
        }
        positionMenuInViewport(anchorRef.current, menuRef.current, {
            preferAlign,
            gap,
            padding,
            matchAnchorWidth
        });
    }, [open, anchorRef, menuRef, preferAlign, gap, padding, matchAnchorWidth]);

    useLayoutEffect(() => {
        if (!open) {
            clearMenuViewportPosition(menuRef.current);
            return undefined;
        }

        update();
        const raf1 = window.requestAnimationFrame(() => {
            update();
            window.requestAnimationFrame(update);
        });

        const menuEl = menuRef.current;
        const resizeObserver = typeof ResizeObserver !== "undefined" && menuEl
            ? new ResizeObserver(() => update())
            : null;
        resizeObserver?.observe(menuEl);

        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        window.visualViewport?.addEventListener("resize", update);
        window.visualViewport?.addEventListener("scroll", update);

        return () => {
            window.cancelAnimationFrame(raf1);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
            window.visualViewport?.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("scroll", update);
            clearMenuViewportPosition(menuRef.current);
        };
    }, [open, update, menuRef]);
}

/** True when the device primarily uses hover (desktop); false on most phones. */
export function canHoverFinePointer() {
    return typeof window !== "undefined"
        && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}
